import React, { useEffect, useRef, useState } from 'react';
import { Moon, HeartCrack, Briefcase, House, Sprout, Waves, Flower2, Flame, Sunrise, Ellipsis, Sparkles, LayoutGrid, ArrowRight, type LucideIcon } from 'lucide-react';

// A small, quiet icon for each Sky feeling (keys from content/skyFeelings.ts).
export const FEELING_ICONS: Record<string, LucideIcon> = {
  all: Sparkles, lonely: Moon, heartbreak: HeartCrack, work: Briefcase, family: House, selfworth: Sprout,
  anxiety: Waves, grief: Flower2, anger: Flame, hope: Sunrise, other: Ellipsis,
};

type Chip = { key: string; label: string; count: number };

// The Sky's feeling chips. When they don't fit (phones), they drift slowly like a
// ticker so people see there are more. A touch, scroll or hover pauses it for a
// while, and it stays still while a feeling is picked. Reduced motion: no drift.
const SPEED = 18; // px per second
const PAUSE_MS = 10_000;
const pauseFor = (until: { current: number }) => { until.current = performance.now() + PAUSE_MS; };

export default function FeelingChips({ chips, value, onChange, isNight, trailing }: {
  chips: Chip[]; value: string; onChange: (key: string) => void; isNight: boolean;
  trailing?: { label: string; onClick: () => void }; // e.g. "All feelings", a link at the end of the row
}) {
  const box = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const pausedUntil = useRef(0);
  const hovering = useRef(false);

  // Does the row fit? Only a row that doesn't fit gets the ticker.
  useEffect(() => {
    const check = () => {
      if (!box.current || !track.current) return;
      setOverflows(track.current.scrollWidth > box.current.clientWidth + 4);
    };
    check();
    const ro = new ResizeObserver(check);
    if (box.current) ro.observe(box.current);
    if (track.current) ro.observe(track.current);
    return () => ro.disconnect();
  }, [chips.length]);

  useEffect(() => {
    if (!overflows) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0, last = performance.now(), carry = 0;
    const step = (now: number) => {
      const el = box.current, first = track.current;
      const dt = Math.min(64, now - last); last = now;
      if (el && first && value === 'all' && !hovering.current && now > pausedUntil.current) {
        carry += (SPEED * dt) / 1000;
        const whole = Math.floor(carry);
        if (whole) { el.scrollLeft += whole; carry -= whole; }
        const loop = first.offsetWidth; // the copy after it makes the loop seamless
        if (el.scrollLeft >= loop) el.scrollLeft -= loop;
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [overflows, value]);

  const pause = () => pauseFor(pausedUntil);

  const chipClass = (active: boolean) => `shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors cursor-pointer ${
    active
      ? (isNight ? 'bg-white text-black border-white' : 'bg-[#1a1a1a] text-white border-[#1a1a1a]')
      : (isNight ? 'bg-black/30 border-white/15 text-white/80 hover:bg-white/10' : 'bg-white/50 border-black/10 text-black/75 hover:bg-white/80')
  }`;
  const row = (copy: boolean) => [...chips.map(c => {
    const Icon = FEELING_ICONS[c.key] ?? Ellipsis;
    const active = value === c.key;
    return (
      <button
        key={`${copy ? 'b' : 'a'}-${c.key}`}
        className={chipClass(active)}
        aria-pressed={copy ? undefined : active}
        tabIndex={copy ? -1 : undefined}
        onClick={() => { pause(); onChange(c.key === value && c.key !== 'all' ? 'all' : c.key); }}
      >
        <Icon size={13} strokeWidth={1.75} className="opacity-60" aria-hidden="true" />
        {c.label}
        <span className="opacity-50 tabular-nums">{c.count}</span>
      </button>
    );
  }), ...(trailing ? [
    <button key={`${copy ? 'b' : 'a'}-trailing`} className={chipClass(false)} tabIndex={copy ? -1 : undefined} onClick={trailing.onClick}>
      <LayoutGrid size={13} strokeWidth={1.75} className="opacity-60" aria-hidden="true" />
      {trailing.label}
      <ArrowRight size={12} className="opacity-60" aria-hidden="true" />
    </button>,
  ] : [])];

  // Keep the picked chip in view while the row is still.
  useEffect(() => {
    if (value === 'all' || !box.current) return;
    const el = box.current.querySelector<HTMLElement>('[aria-pressed="true"]');
    el?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [value]);

  return (
    <div
      ref={box}
      role="group"
      aria-label="Show thoughts by feeling"
      className="pointer-events-auto flex overflow-x-auto no-scrollbar max-w-full rounded-full"
      style={overflows ? { maskImage: 'linear-gradient(90deg, transparent, #000 24px, #000 calc(100% - 24px), transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 24px, #000 calc(100% - 24px), transparent)' } : undefined}
      onPointerDown={pause}
      onWheel={pause}
      onTouchStart={pause}
      onFocus={pause}
      onMouseEnter={() => { hovering.current = true; }}
      onMouseLeave={() => { hovering.current = false; pause(); }}
      onScroll={() => {
        const el = box.current, first = track.current;
        if (el && first && overflows && el.scrollLeft >= first.offsetWidth) el.scrollLeft -= first.offsetWidth;
      }}
    >
      {overflows && <div className="shrink-0 w-5" aria-hidden="true" />}
      <div ref={track} className="flex gap-1.5 shrink-0 pr-1.5">{row(false)}</div>
      {overflows && <div className="flex gap-1.5 shrink-0 pr-1.5" aria-hidden="true">{row(true)}</div>}
    </div>
  );
}
